import React, { memo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUID } from 'react-uid';
import qs from 'query-string-for-all';
import { useLocation, useHistory } from 'react-router-dom';

import LoginPromoModal from '../../../../components/modal/login-promo';
import UserReviewDialog from '../dialog';
import StoreReviewForm from '../store-form';
import ThankYouDialog from '../thank-you';

import * as S from './styles';

import { openReviewDialog, closeReviewDialog } from '../../actions';

import { AppState } from '../../../../reducers';
import { BelongsTo, Review, Store, Action } from '../../types';
import { useIsAuthenticated } from '../../../../hooks/use-is-authenticated';

type Props = {
  className?: string;
  firstToReview?: boolean;
  firstToReviewText?: string;
  control: React.ReactElement;
  belongsTo: BelongsTo;
  currentReview?: Review;
  myReview?: Review;
  store?: Store | null;
};

const selectReviewDialogOpenId = (state: AppState) =>
  state.areas.review.dialog.openId;
const selectThankYouDialogOpenId = (state: AppState) =>
  state.areas.review.thankYouDialog.openId;

const ReviewPrompt = ({
  className,
  firstToReview = false,
  firstToReviewText,
  control,
  belongsTo,
  currentReview,
  store,
}: Props) => {
  const [showLoginPromo, setShowLoginPromo] = useState(false);
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const { push } = useHistory();
  const isAuthenticated = useIsAuthenticated();
  const reviewDialogOpenId = useSelector(selectReviewDialogOpenId);
  const thankYouDialogOpenId = useSelector(selectThankYouDialogOpenId);
  const uid = useUID();

  const {
    review: reviewParam,
    review_id: anonReviewId,
    code,
    ...queryParams
  } = qs.parse(search);

  const isStorePage = /((butiksinfo|store|shop).php)|(--s\d)/.test(pathname);

  const actionType =
    currentReview || (code && anonReviewId) ? Action.EDIT : Action.WRITE;

  const clickHandler = useCallback(() => {
    if (belongsTo.type === 'product' && !isAuthenticated) {
      setShowLoginPromo(true);
    } else {
      dispatch(openReviewDialog(uid));

      if (belongsTo.type === 'store' && isStorePage) {
        if (actionType === Action.WRITE) {
          push(
            decodeURIComponent(
              `${pathname}?${qs.stringify({
                ...queryParams,
                review: Action.WRITE,
              })}`
            )
          );
        } else {
          push(
            decodeURIComponent(
              `${pathname}?${qs.stringify({
                ...queryParams,
                review: Action.EDIT,
              })}`
            )
          );
        }
      }
    }
  }, [
    isAuthenticated,
    dispatch,
    uid,
    belongsTo.type,
    pathname,
    queryParams,
    push,
    isStorePage,
    actionType,
  ]);

  const closeLoginPromo = useCallback(() => {
    setShowLoginPromo(false);
  }, []);

  const shouldOpenWriteReview =
    reviewParam === Action.WRITE && actionType === Action.WRITE;
  const shouldOpenEditReview =
    actionType === Action.EDIT && reviewParam === Action.EDIT;

  useEffect(() => {
    if (!isStorePage) return;

    // Open the review form dialog when landing on a page with the query param `review`
    if (shouldOpenWriteReview || shouldOpenEditReview) {
      if (!reviewDialogOpenId) {
        dispatch(openReviewDialog(uid));
      }
    }

    // Close review form when query param `review` is removed
    if (
      !shouldOpenWriteReview &&
      !shouldOpenEditReview &&
      reviewDialogOpenId === uid
    ) {
      dispatch(closeReviewDialog());
    }
  }, [reviewParam]);

  const anonymousReview =
    code && anonReviewId
      ? {
          id: anonReviewId as string,
          code: code as string,
        }
      : undefined;

  return (
    <>
      {belongsTo.type === 'product' && (
        <UserReviewDialog
          id={uid}
          belongsTo={belongsTo}
          review={currentReview}
        />
      )}

      {belongsTo.type === 'store' && store && reviewDialogOpenId === uid && (
        <StoreReviewForm
          uid={uid}
          store={store}
          review={currentReview || anonymousReview}
        />
      )}

      {belongsTo.type === 'store' && thankYouDialogOpenId === uid && (
        <ThankYouDialog uid={uid} />
      )}

      <S.UserReviewPrompt className={className}>
        {firstToReview && (
          <S.FirstToReview>{firstToReviewText}</S.FirstToReview>
        )}

        {!isAuthenticated && showLoginPromo ? (
          <LoginPromoModal
            isOpen={showLoginPromo}
            onRequestClose={closeLoginPromo}
          />
        ) : null}

        {React.cloneElement(control, {
          onClick: clickHandler,
        })}
      </S.UserReviewPrompt>
    </>
  );
};

export default memo(ReviewPrompt);
